import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie, Info, Settings, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CookiePolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      {/* Hero Section */}
      <section className="relative bg-[#0d6571] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white/80 mb-8 -ml-4"
          >
            <ArrowLeft className="mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <Cookie className="w-16 h-16 text-[#d97841]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Cookie Policy
            </h1>
          </div>
          
          <p className="text-xl text-white/90 max-w-4xl leading-relaxed">
            Understanding how we use cookies and similar technologies on our website
          </p>
          <p className="text-sm text-white/70 mt-4">
            Last Updated: November 2024
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Introduction */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                This Cookie Policy explains how FXC Management Services B.V. ("we", "us", or "our") uses 
                cookies and similar technologies when you visit our website. This policy provides you 
                with information about what cookies are, how we use them, and how you can manage your 
                cookie preferences.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                By using our website, you agree to the use of cookies as described in this policy.
              </p>
            </div>
          </div>

          {/* What Are Cookies */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Info className="w-8 h-8 text-[#d97841]" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                What Are Cookies?
              </h2>
            </div>
            
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Cookies are small text files that are placed on your device (computer, tablet, or mobile) 
                when you visit a website. They are widely used to make websites work more efficiently and 
                provide information to website owners.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Cookies help us understand how visitors use our site, remember your preferences, and 
                improve your overall experience. They can store information such as your language preference, 
                login status, or items in your shopping cart.
              </p>
            </div>
          </div>

          {/* Types of Cookies */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Types of Cookies We Use
            </h2>
            
            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
                <div className="flex items-start gap-4">
                  <div className="bg-[#0d6571] p-3 rounded-lg flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      1. Necessary Cookies (Essential)
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      These cookies are essential for the website to function properly. They enable basic 
                      functions like page navigation, access to secure areas, and maintaining your session.
                    </p>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-sm font-semibold text-gray-900 mb-2">Examples:</p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>Session cookies that keep you logged in</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>Security cookies for authentication</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>Cookie consent preferences</span>
                        </li>
                      </ul>
                      <p className="text-xs text-gray-500 italic mt-3">
                        These cookies cannot be disabled as they are necessary for the website to work.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#d97841] p-8 rounded-r-lg">
                <div className="flex items-start gap-4">
                  <div className="bg-[#d97841] p-3 rounded-lg flex-shrink-0">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      2. Analytics Cookies (Performance)
                    </h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      These cookies help us understand how visitors interact with our website by collecting 
                      and reporting information anonymously. This helps us improve our website's performance 
                      and user experience.
                    </p>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-sm font-semibold text-gray-900 mb-2">What we collect:</p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>Pages visited and time spent on pages</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>How you arrived at our site (referral source)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>Browser type and device information</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-[#d97841] mr-2">•</span>
                          <span>Click patterns and navigation paths</span>
                        </li>
                      </ul>
                      <p className="text-xs text-gray-500 italic mt-3">
                        We use Google Analytics and similar tools. All data is anonymized.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  3. Marketing Cookies (Targeting)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These cookies are used to track visitors across websites to display ads that are relevant 
                  and engaging. They help us measure the effectiveness of advertising campaigns.
                </p>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Used for:</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Displaying personalized advertisements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Limiting how often you see an ad</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Measuring advertising campaign effectiveness</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Retargeting visitors who have shown interest</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Personalization Cookies */}
              <div className="bg-gradient-to-r from-white to-[#0d6571]/5 border-l-4 border-[#d97841] p-8 rounded-r-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  4. Personalization Cookies (Functional)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These cookies allow the website to remember choices you make and provide enhanced, 
                  personalized features based on your preferences.
                </p>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Features enabled:</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Remembering your language preference</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Saving your region or location</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Customizing content based on your interests</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#d97841] mr-2">•</span>
                      <span>Remembering display preferences</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Managing Cookies */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Managing Your Cookie Preferences
            </h2>
            
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 rounded-2xl p-8 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Using Our Cookie Consent Tool
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you first visit our website, you'll see a cookie consent banner. You can:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-xl">✓</span>
                  <span><strong>Accept All:</strong> Allow all cookies for the best experience</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-xl">✓</span>
                  <span><strong>Reject Non-Essential:</strong> Only use necessary cookies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-xl">✓</span>
                  <span><strong>Manage Preferences:</strong> Choose which cookie categories to allow</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Browser Settings
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You can also control cookies through your browser settings:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span><strong>Google Chrome:</strong> Settings → Privacy and security → Cookies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span><strong>Firefox:</strong> Options → Privacy & Security → Cookies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span><strong>Safari:</strong> Preferences → Privacy → Cookies</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span><strong>Edge:</strong> Settings → Privacy → Cookies</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500 italic mt-4">
                Note: Blocking all cookies may affect your ability to use some features of our website.
              </p>
            </div>
          </div>

          {/* Third-Party Cookies */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Third-Party Cookies
            </h2>
            
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Some cookies on our website are set by third-party services that appear on our pages. 
                We use trusted third-party services for analytics, advertising, and functionality.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Common third-party cookies include:
              </p>
              <ul className="space-y-2 text-gray-600 mb-4">
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span>Google Analytics (analytics)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span>Google Ads (advertising)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-2">•</span>
                  <span>Social media platforms (sharing and widgets)</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                These third parties have their own privacy policies. We recommend reviewing them to 
                understand how they use your data.
              </p>
            </div>
          </div>

          {/* Updates to Policy */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Updates to This Policy
            </h2>
            
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
              <p className="text-lg text-gray-700 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for operational, legal, or regulatory reasons. We will notify you of any significant 
                changes by posting the new policy on this page with an updated "Last Updated" date.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-[#0d6571] rounded-2xl p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Questions About Cookies?
            </h2>
            <p className="text-lg text-white/90 mb-6">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="bg-white/10 rounded-xl p-6">
              <p className="mb-2"><strong>FXC Management Services B.V.</strong></p>
              <p className="mb-2">Industrieweg 9, 2254AE Voorschoten, The Netherlands</p>
              <p className="mb-2">Email: <a href="mailto:info@fxcvantage.com" className="text-[#d97841] hover:underline">info@fxcvantage.com</a></p>
              <p>Chamber of Commerce: 78204992</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
